import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, Sun, Moon, Cloud, Snowflake, Flower2, Leaf,
  ExternalLink, Sparkles, ShoppingBag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useFavorites } from "@/context/FavoritesContext";
import axios from "axios";

import { API, BACKEND_URL } from "../apiConfig";
import LOGO_URL from "../img/sapphire.svg";

const seasonIcons = {
  Invierno: Snowflake,
  Primavera: Flower2,
  Verano: Sun,
  Otoño: Leaf,
  Día: Sun,
  Noche: Moon,
};

const accordColors = [
  "bg-rose-50 text-rose-700 border-rose-100",
  "bg-amber-50 text-amber-700 border-amber-100",
  "bg-emerald-50 text-emerald-700 border-emerald-100",
  "bg-sky-50 text-sky-700 border-sky-100",
  "bg-violet-50 text-violet-700 border-violet-100",
  "bg-orange-50 text-orange-700 border-orange-100",
  "bg-teal-50 text-teal-700 border-teal-100",
  "bg-pink-50 text-pink-700 border-pink-100",
  "bg-indigo-50 text-indigo-700 border-indigo-100",
  "bg-lime-50 text-lime-700 border-lime-100",
];

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// ─── Recommendation Card Component ───────────────────────────
const RecommendationCard = ({ perfume }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgErr, setImgErr] = useState(false);
  const ph = "https://images.unsplash.com/photo-1618436623662-6043c7cb58df?w=300&h=400&fit=crop";
  
  return (
    <Link to={`/perfume/${perfume.id}`} className="group block" data-testid={`rec-card-${perfume.id}`}>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300 overflow-hidden">
        <div className="bg-[#FFFFFF] p-4 aspect-[3/4] flex items-center justify-center overflow-hidden">
          <img
            src={imgErr ? ph : (perfume.imagen_url || ph)}
            alt={perfume.nombre}
            className={`max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImgLoaded(true)}
            onError={() => { setImgErr(true); setImgLoaded(true); }}
          />
        </div>
        <div className="p-4">
          <p className="text-[10px] font-semibold tracking-wider text-slate-500 uppercase">{perfume.marca}</p>
          <h4 className="font-serif text-sm text-slate-900 leading-tight line-clamp-2 mt-0.5">{perfume.nombre}</h4>
        </div>
      </div>
    </Link>
  );
};

// ─── Main Perfume Detail Component ───────────────────────────
const PerfumeDetail = () => {
  const { id } = useParams();
  const [perfume, setPerfume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const { toggleFavorite, isFavorite, getFavoritesCount } = useFavorites();
  const navigate = useNavigate();
  const favCount = getFavoritesCount();

  const placeholderImage = "https://images.unsplash.com/photo-1618436623662-6043c7cb58df?w=600&h=750&fit=crop";

  useEffect(() => {
    const fetchPerfume = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log(`📥 Cargando perfume de: ${API}/perfumes/${id}`);
        const response = await axios.get(`${API}/perfumes/${id}`);
        console.log("✅ Perfume recibido:", response.data);
        setPerfume(response.data);
        
        // Fetch recommendations from same family
        try {
          const familia = response.data.familia_olfativa || "";
          const searchTerm = familia.split(' ')[0];
          console.log(`📥 Buscando recomendaciones para: ${searchTerm}`);
          
          const recRes = await axios.get(`${API}/perfumes?page=1&page_size=50&search=${encodeURIComponent(searchTerm)}`);
          console.log("✅ Recomendaciones recibidas:", recRes.data);
          
          const others = recRes.data.perfumes.filter((p) => p.id !== id);
          // Shuffle and pick 4
          const shuffled = others.sort(() => 0.5 - Math.random());
          setRecommendations(shuffled.slice(0, 4));
        } catch (err) {
          console.error("⚠️ Error cargando recomendaciones:", err);
          setRecommendations([]);
        }
      } catch (err) {
        console.error("❌ Error cargando perfume:", err);
        setError(`Error cargando perfume: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchPerfume();
    }
  }, [id]);

  // Scroll to top when detail page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const liked = perfume ? isFavorite(perfume.id) : false;

  const getWhatsAppLink = () => {
    if (!perfume) return "#";
    const message = encodeURIComponent(`Hola! Me interesa el perfume: ${perfume.nombre} de ${perfume.marca}`);
    return `https://wa.me/525547630396?text=${message}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF]">
        <header className="glass-header sticky top-0 z-50 border-b border-white/40 shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center gap-4">
              <Skeleton className="w-10 h-10 rounded-full" />
              <Skeleton className="w-32 h-6" />
            </div>
          </div>
        </header>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid md:grid-cols-2 gap-12">
            <Skeleton className="aspect-[4/5] rounded-3xl" />
            <div className="space-y-6">
              <Skeleton className="h-8 w-24" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-14 w-full rounded-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!perfume) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 text-lg mb-4">
            {error ? error : "Perfume no encontrado"}
          </p>
          <p className="text-slate-400 text-sm mb-6">Backend URL: {BACKEND_URL}</p>
          <Link to="/">
            <Button variant="outline" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver al catálogo
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const votos = perfume.cuando_usarlo || perfume.votos_estaciones || {};
  const sortedWhenToUse = Object.entries(votos)
  .filter(([_, val]) => val > 0)
  .sort((a, b) => b[1] - a[1]); // Ordena para que el más votado salga primero
  const hasWhenToUse = sortedWhenToUse.length > 0;

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 border-b border-white/40 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-sm font-medium">Volver</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/wishlist" className="relative" data-testid="wishlist-header-link-detail">
                <ShoppingBag className={`w-5 h-5 ${favCount > 0 ? "fill-white text-rose-500" : "text-slate-500"}`} strokeWidth={1.5} />
                {favCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {favCount}
                  </span>
                )}
              </Link>
              <Link to="/" className="flex items-center gap-2" data-testid="logo">
                <img src={LOGO_URL} alt="Sapphire Spell" className="h-[72px] object-contain" />
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-6 py-8 md:py-12">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-16">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="bg-white rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] overflow-hidden">
              <div className="aspect-[4/5] bg-[#FFFFFF] p-8 flex items-center justify-center relative">
                {!imageLoaded && !imageError && (
                  <Skeleton className="absolute inset-8 rounded-2xl skeleton-shimmer" />
                )}
                <img
                  src={imageError ? placeholderImage : (perfume.imagen_url || placeholderImage)}
                  alt={perfume.nombre}
                  className={`max-h-full max-w-full object-contain transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => { setImageError(true); setImageLoaded(true); }}
                  data-testid="perfume-image"
                />
              </div>
            </div>
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex flex-col"
          >
            {/* Badges — clickable to filter */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {perfume.tipo_marca && (
                <Link to={`/?tipo_marca=${encodeURIComponent(perfume.tipo_marca)}`}>
                  <Badge
                    className="bg-rose-50 text-rose-700 border border-rose-100 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wider cursor-pointer hover:bg-rose-100 transition-colors"
                    data-testid="perfume-tipo-marca"
                  >
                    {perfume.tipo_marca}
                  </Badge>
                </Link>
              )}
              <Link to={`/?genero=${encodeURIComponent(perfume.genero)}`}>
                <Badge
                  variant="outline"
                  className="rounded-full px-4 py-1.5 text-xs font-semibold tracking-wider cursor-pointer hover:bg-slate-50 transition-colors"
                  data-testid="perfume-genero"
                >
                  {perfume.genero === "Caballero" ? "Hombre" : "Mujer"}
                </Badge>
              </Link>
              {perfume.novedad && (
                <Link to="/?novedad=true">
                  <Badge
                    className="bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wider cursor-pointer hover:bg-amber-100 transition-colors"
                    data-testid="perfume-novedad"
                  >
                    Novedad
                  </Badge>
                </Link>
              )}
            </div>

            <p className="text-sm font-semibold tracking-wider text-slate-500 uppercase mb-2">
              {perfume.marca}
            </p>
            <h1
              className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-slate-900 tracking-tight mb-4"
              data-testid="perfume-nombre"
            >
              {perfume.nombre}
            </h1>
            <p className="text-lg text-slate-600 mb-4">
              {perfume.familia_olfativa}
            </p>

            {/* Add to order Button */}
            <Button
              className={`rounded-full mb-6 self-start transition-all duration-300 text-base px-8 py-5 ${
                liked
                  ? "bg-slate-900 hover:bg-slate-800 text-white shadow-lg shadow-slate-900/20"
                  : "bg-rose-500 hover:bg-rose-600 text-white shadow-lg shadow-rose-500/30"
              }`}
              onClick={() => toggleFavorite(perfume)}
              data-testid="detail-fav-btn"
            >
              <ShoppingBag className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {liked ? "En tu pedido" : "Agregar a mi pedido"}
            </Button>

            <Separator className="my-2" />

            {/* Acordes */}
            {perfume.acordes_principales && perfume.acordes_principales.length > 0 && (
              <div className="mb-6 mt-4">
                <h2 className="font-serif text-xl font-medium text-slate-900 mb-4">
                  Acordes principales
                </h2>
                <div className="flex flex-wrap gap-2" data-testid="acordes-section">
                  {perfume.acordes_principales.map((acorde, index) => (
                    <Badge
                      key={index}
                      className={`${accordColors[index % accordColors.length]} border rounded-full px-4 py-1.5 text-sm font-medium pointer-events-none`}
                      data-testid={`acorde-${index}`}
                    >
                      {acorde}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Cuando Usarlo */}
            {hasWhenToUse && (
              <div className="mb-6">
                <h2 className="font-serif text-xl font-medium text-slate-900 mb-4">
                  Cuándo usarlo
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="cuando-usarlo-section">
                  {sortedWhenToUse.slice(0, 6).map(([season, votes]) => {
                    const SeasonIcon = seasonIcons[season] || Cloud;
                    const maxVotes = Math.max(...sortedWhenToUse.map(([, v]) => v));
                    const percentage = Math.round((votes / maxVotes) * 100);
                    return (
                      <div
                        key={season}
                        className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"
                        data-testid={`season-${season.toLowerCase()}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <SeasonIcon className="w-4 h-4 text-slate-500" strokeWidth={1.5} />
                          <span className="text-sm font-medium text-slate-700">{season}</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div
                            className="bg-rose-400 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Description */}
            {perfume.descripcion && (
              <div className="mb-6">
                <h2 className="font-serif text-xl font-medium text-slate-900 mb-4">
                  Descripción
                </h2>
                <p className="text-slate-600 leading-relaxed" data-testid="perfume-descripcion">
                  {perfume.descripcion}
                </p>
              </div>
            )}

            {/* Fragrantica Link */}
            {perfume.url_fragrantica && (
              <a
                href={perfume.url_fragrantica}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-8 transition-colors"
                data-testid="fragrantica-link"
              >
                <ExternalLink className="w-4 h-4" strokeWidth={1.5} /> Ver más información en Fragrantica
              </a>
            )}

            {/* WhatsApp CTA */}
            <div className="mt-auto pt-6">
              <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="block" data-testid="whatsapp-cta-button">
                <Button
                  className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full py-6 text-lg font-medium shadow-lg shadow-[#25D366]/30 whatsapp-pulse transition-colors"
                  size="lg"
                >
                  <WhatsAppIcon className="w-6 h-6 mr-3" /> Preguntar por este perfume
                </Button>
              </a>
              <p className="text-center text-sm text-slate-500 mt-3">
                Te responderemos lo antes posible
              </p>
            </div>
          </motion.div>
        </div>

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <section className="mt-16">
            <h2 className="font-serif text-2xl font-medium text-slate-900 mb-8">
              Perfumes similares
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
              {recommendations.map((rec) => (
                <RecommendationCard key={rec.id} perfume={rec} />
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Mobile Fixed WhatsApp */}
      <div className="fixed bottom-6 right-6 md:hidden z-50">
        <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-mobile-btn">
          <Button className="w-16 h-16 rounded-full bg-[#25D366] hover:bg-[#1DA851] shadow-lg shadow-[#25D366]/30 whatsapp-pulse" size="icon">
            <WhatsAppIcon className="w-8 h-8" />
          </Button>
        </a>
      </div>
    </div>
  );
};

export default PerfumeDetail;
